import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FloatingAudioPlayerProps {
  src: string | null | undefined;
  title?: string;
  onClose?: () => void;
}

export default function FloatingAudioPlayer({ src, title, onClose }: FloatingAudioPlayerProps) {
  const { t } = useTranslation(['common']);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setTime(audio.currentTime || 0);
    const onLoaded = () => setDuration(audio.duration || 0);
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('ended', onEnded);
    };
  }, [src]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
  }, [volume]);

  useEffect(() => {
    // Auto-play when a new src is set
    const audio = audioRef.current;
    if (!audio) return;
    if (src) {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    } else {
      setIsPlaying(false);
      setTime(0);
      setDuration(0);
    }
  }, [src]);

  if (!src) return null;

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      void audio.play();
      setIsPlaying(true);
    }
  };

  const seek = (e: ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const val = Number(e.target.value);
    audio.currentTime = val;
    setTime(val);
  };

  const toTime = (s: number): string => {
    if (!Number.isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  return (
    <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-50">
      <div className="mx-auto sm:ml-auto sm:w-[360px] bg-white/95 backdrop-blur border border-gray-200 shadow-xl rounded-xl p-3">
        <div className="flex items-center gap-3">
          <Button size="icon" variant="ghost" onClick={togglePlay} className="shrink-0">
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </Button>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 truncate">{title || t('common:audioPlayer.defaultTitle')}</div>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="range"
                min={0}
                max={duration || 0}
                value={Math.min(time, duration || 0)}
                onChange={seek}
                className="w-full h-1 accent-purple-600"
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{toTime(time)}</span>
              <span>{toTime(duration)}</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 ml-1">
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="w-20 h-1 accent-gray-400"
              aria-label={t('common:audioPlayer.volume')}
            />
            <Button size="icon" variant="ghost" onClick={onClose} className="shrink-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <audio ref={audioRef} src={src} preload="metadata" />
      </div>
    </div>
  );
}